// Task-flow analytics (v3-roadmap.md item 5) — Jira only, live-computed over the changelog.

export interface StatusDuration {
  status: string;
  avgDays: number;
  medianDays: number;
  sampleCount: number;
}

export interface TransitionFrequency {
  from: string;
  to: string;
  count: number;
}

export interface ProjectFlowSummary {
  projectId: string;
  range: { from: string; to: string };
  statusDurations: StatusDuration[]; // sorted worst (slowest) first
  transitionFrequency: TransitionFrequency[]; // sorted most-common first, top 15
  bottleneckStatus: string | null; // = statusDurations[0]?.status
}

export interface TaskTransitionStep {
  status: string | null; // null only if the very first step's prior status is unknown
  enteredAt: string; // ISO
  exitedAt: string | null; // null = current/active step
  durationDays: number; // for the active step, duration so far (against "now")
}

export interface TaskTransitionTimeline {
  issueId: string;
  issueKey?: string;
  currentStatus?: string;
  steps: TaskTransitionStep[];
  reopenCount: number;
}

export type AnomalyReason = 'status_duration_outlier' | 'frequent_reopens' | 'frequent_reassignment';

export interface AnomalousTaskDTO {
  issueId: string;
  issueKey?: string;
  summary?: string;
  status?: string;
  assigneeAccountId?: string;
  assigneeName?: string;
  daysInCurrentStatus: number;
  statusTypicalMedianDays: number | null;
  deviationRatio: number | null; // daysInCurrentStatus / statusTypicalMedianDays
  reopenEvents: number;
  assigneeChanges: number;
  anomalyReasons: AnomalyReason[];
}

export const ANOMALY_REASON_LABEL: Record<AnomalyReason, string> = {
  status_duration_outlier: 'Stuck in status',
  frequent_reopens: 'Frequently reopened',
  frequent_reassignment: 'Frequently reassigned',
};

// GET /api/dashboard/tasks/:issueId — real issue fields for the task detail page, not just the
// timeline. `comments` is always `[]` today — Jira issue comments aren't reliably synced yet
// (rest-api.md "Jira task detail", 2026-08-17).
export interface IssueDetailDTO {
  issueId: string;
  issueKey?: string;
  summary?: string;
  description: string | null;
  status?: string;
  priority?: string;
  assigneeAccountId?: string;
  assigneeName?: string;
  reporterName?: string;
  labels: string[];
  createdAt?: string;
  updatedAt?: string;
  comments: Array<{ id: string; text: string; createdAt: string }>;
}
